import {
  getLocation,
  getLocationOrThrow,
} from "@faire/web/common/globals/getLocation";
import {
  EnvValues,
  getForEnv,
} from "@faire/web/common/user-agent/getForEnv";
import { isLocal } from "@faire/web/common/user-agent/isLocal";
import { isPrerelease } from "@faire/web/common/user-agent/isPrerelease";
import { isProduction } from "@faire/web/common/user-agent/isProduction";
import { isStaging } from "@faire/web/common/user-agent/isStaging";

type LocalApp =
  | "retailer"
  | "brand"
  | "admin"
  | "developers"
  | "d2c"
  | "widget"
  | "blog"
  | "help-center"
  | "visitor"
  | "releases"
  | "experiments"
  | "goose";

export const LOCAL_PORT: { [K in LocalApp]: number } = {
  retailer: 3000,
  brand: 3001,
  admin: 3003,
  developers: 3004,
  d2c: 3005,
  widget: 3006,
  blog: 3007,
  "help-center": 3008,
  visitor: 3009,
  releases: 3010,
  experiments: 3011,
  goose: 3012,
  // web: 3013, // web doesn't have a dev server but it has a storybook on 4013
} as const;
type LocalAppPort = (typeof LOCAL_PORT)[keyof typeof LOCAL_PORT];

const SUBDOMAINS: Partial<Record<LocalAppPort, string>> = {
  [LOCAL_PORT.developers]: "developers",
  [LOCAL_PORT.blog]: "blog",
} as const;

const isFriendlyName = (spec?: LocalApp | LocalAppPort): spec is LocalApp => {
  return !!spec && spec in LOCAL_PORT;
};

export function faireBaseUrl(port?: LocalAppPort): string;
export function faireBaseUrl(app?: LocalApp): string;

export function faireBaseUrl(
  nameOrLocalhostPort?: LocalApp | LocalAppPort
): string {
  const port: LocalAppPort | undefined = isFriendlyName(nameOrLocalhostPort)
    ? LOCAL_PORT[nameOrLocalhostPort]
    : nameOrLocalhostPort;
  const subdomain = (!!port && SUBDOMAINS[port]) || "www";
  if (isProduction()) {
    return `https://${subdomain}.faire.com`;
  } else if (isStaging()) {
    return `https://${subdomain}.faire-stage.com`;
  } else if (isPrerelease()) {
    return `https://${subdomain}.prerelease.faire.dev`;
  } else if (isLocal() && port) {
    return `http://localhost:${port}`;
  }
  return getLocation()?.origin ?? "";
}

const HOST_SUFFIX_BY_ENV: EnvValues<string | null> = {
  defaultValue: null,
  production: "faire.com",
  staging: "faire-stage.com",
  local: "localhost",
} as const;

export const isValidFaireUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url, getLocationOrThrow().href);
    const domainSuffix = getDomainSuffix(parsedUrl.hostname);

    const validHostSuffix =
      getForEnv(HOST_SUFFIX_BY_ENV) ??
      getDomainSuffix(getLocationOrThrow().hostname);
    return domainSuffix === validHostSuffix;
  } catch {
    return false;
  }
};

const getDomainSuffix = (hostname: string) => {
  // Because Faire can be served on subdomains (subdomain.faire.com),
  //  we need to take the last 2 segments of the host name (faire.com)
  //  and check if its valid.
  const hostnameSegments = hostname.split(".");
  return hostnameSegments
    .slice(Math.max(hostnameSegments.length - 2, 0))
    .join(".");
};
